import { COMPONENT_COLOR_THEMES, COMPONENT_VALUE_TYPES as types } from 'shared/constants';
import { Theme } from 'shared/types';
import type { PropsSchemaList } from 'shared/types/PropsSchema';

export type Position = 'TOP' | 'BOTTOM' | 'LEFT' | 'RIGHT' | 'CENTER';
export type Align = 'LEFT' | 'CENTER' | 'RIGHT';

export interface Props {
  backgroundImage?: {
    _?: string;
    md?: string;
    imageAlt?: string;
  };
  textColorTheme?: Theme;
  backNavigation?: boolean;
  variant1?: {
    enabled?: boolean;
    title?: string;
    description?: string;
    footnote?: string;
    descriptionItems?: {
      title?: string;
      description?: string;
    }[];
    textBoxGradient?: {
      linearGradient?: string;
    };
  };
  variant2?: {
    enabled?: boolean;
    title?: string;
    description?: string;
    subtitle?: string;
    textAlign?: {
      _: Align,
      md: Align
    };
    descriptionItems?: {
      title?: string;
      subtitle?: string;
    }[];
    position?: {
      _: Position,
      md: Position
    };
  };
  variant3?: {
    enabled?: boolean;
    title?: string;
    subtitle?: string;
    descriptionTitle?: string;
    descriptionItems?: {
      title?: string;
      subtitle?: string;
    }[];
  };
  variant4?: {
    enabled?: boolean;
    title?: string;
    subtitle?: string;
    descriptionItems?: {
      title?: string;
      backgroundImage?: string;
      bulletPoints?: {
        text?: string;
      }[];
    }[];
  };
  variant5?: {
    enabled?: boolean;
    title?: string;
    leftColumn?: {
      column?: {
        text?: string
      }[];
    },
    rightColumn?: {
      column?: {
        text?: string
      }[],
    },
  };
  confluence?: string;
}

const propsSchema: PropsSchemaList = [
  {
    key: 'backgroundImage',
    label: 'Background Image',
    type: types.OBJECT,
    children: [
      {
        key: '_',
        label: 'Mobile (or Base)',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'md',
        label: 'Desktop',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'imageAlt',
        label: 'Image Alternative Text (A brief description of the image to optimize search engine visibility)',
        type: types.TEXT,
      },
    ],
  },
  {
    key: 'textColorTheme',
    label: 'Text Theme',
    type: types.ENUM,
    options: COMPONENT_COLOR_THEMES,
  },
  {
    key: 'backNavigation',
    label: 'Enabled back navigation for App',
    type: types.BOOLEAN,
  },
  {
    key: 'variant1',
    label: 'Variant 1 (Charging Page)',
    type: types.OBJECT,
    children: [
      {
        key: 'enabled',
        label: 'Enabled',
        type: types.BOOLEAN,
      },
      {
        key: 'title',
        label: 'Title',
        type: types.TEXT,
      },
      {
        key: 'description',
        label: 'Description',
        type: types.TEXT,
      },
      {
        key: 'footnote',
        label: 'Footnote',
        type: types.TEXT,
      },
      {
        key: 'descriptionItems',
        label: 'Description Items',
        type: types.ARRAY,
        children: [
          {
            key: 'title',
            label: 'Title',
            type: types.TEXT,
          },
          {
            key: 'description',
            label: 'Description',
            type: types.TEXT,
          },
        ],
      },
      {
        key: 'textBoxGradient',
        label: 'Text Box Gradient',
        type: types.OBJECT,
        children: [
          {
            key: 'linearGradient',
            label: 'Linear Gradient',
            type: types.TEXT,
          },
        ],
      },
    ],
  },
  {
    key: 'variant2',
    label: 'Variant 2 (AI Hypercar Page)',
    type: types.OBJECT,
    children: [
      {
        key: 'enabled',
        label: 'Enabled',
        type: types.BOOLEAN,
      },
      {
        key: 'title',
        label: 'Title',
        type: types.TEXT,
      },
      {
        key: 'subtitle',
        label: 'Subtitle',
        type: types.TEXT,
      },
      {
        key: 'description',
        label: 'Description',
        type: types.TEXT,
      },
      {
        key: 'textAlign',
        label: 'Text Align',
        type: types.OBJECT,
        children: [
          {
            key: '_',
            label: 'Mobile',
            type: types.ENUM,
            options: ['LEFT', 'CENTER', 'RIGHT'],
          },
          {
            key: 'md',
            label: 'Desktop',
            type: types.ENUM,
            options: ['LEFT', 'CENTER', 'RIGHT'],
          },
        ],
      },
      {
        key: 'descriptionItems',
        label: 'Description Items',
        type: types.ARRAY,
        children: [
          {
            key: 'title',
            label: 'Title',
            type: types.TEXT,
          },
          {
            key: 'subtitle',
            label: 'subtitle',
            type: types.TEXT,
          },
        ],
      },
      {
        key: 'position',
        label: 'Position',
        type: types.OBJECT,
        children: [
          {
            key: '_',
            label: 'Mobile',
            type: types.ENUM,
            // center is default
            options: ['CENTER', 'TOP', 'BOTTOM'],
          },
          {
            key: 'md',
            label: 'Desktop',
            type: types.ENUM,
            // center is default
            options: ['CENTER', 'TOP', 'BOTTOM', 'LEFT', 'RIGHT'],
          },
        ],
      },
    ],
  },
  {
    key: 'variant3',
    label: 'Variant 3 (AI Hypercar Page)',
    type: types.OBJECT,
    children: [
      {
        key: 'enabled',
        label: 'Enabled',
        type: types.BOOLEAN,
      },
      {
        key: 'title',
        label: 'Title',
        type: types.TEXT,
      },
      {
        key: 'subtitle',
        label: 'Subtitle',
        type: types.TEXT,
      },
      {
        key: 'descriptionTitle',
        label: 'Description Title',
        type: types.TEXT,
      },
      {
        key: 'descriptionItems',
        label: 'Description Items',
        type: types.ARRAY,
        children: [
          {
            key: 'title',
            label: 'Title',
            type: types.TEXT,
          },
          {
            key: 'subtitle',
            label: 'subtitle',
            type: types.TEXT,
          },
        ],
      },
    ],
  },
  {
    key: 'variant4',
    label: 'Variant 4 (AI Hypercar Page)',
    type: types.OBJECT,
    children: [
      {
        key: 'enabled',
        label: 'Enabled',
        type: types.BOOLEAN,
      },
      {
        key: 'title',
        label: 'Title',
        type: types.TEXT,
      },
      {
        key: 'subtitle',
        label: 'Subtitle',
        type: types.TEXT,
      },
      {
        key: 'descriptionItems',
        label: 'Description Items',
        type: types.ARRAY,
        children: [
          {
            key: 'title',
            label: 'Title',
            type: types.TEXT,
          },
          {
            key: 'backgroundImage',
            label: 'Background Image',
            type: types.S3_ASSET_URL,
          },
          {
            key: 'bulletPoints',
            label: 'Bullet Points',
            type: types.ARRAY,
            children: [
              {
                key: 'text',
                label: 'Text',
                type: types.TEXT,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    key: 'variant5',
    label: 'Variant 5 (AI Hypercar Page)',
    type: types.OBJECT,
    children: [
      {
        key: 'enabled',
        label: 'Enabled',
        type: types.BOOLEAN,
      },
      {
        key: 'title',
        label: 'Title',
        type: types.TEXT,
      },
      {
        key: 'leftColumn',
        label: 'Left Column',
        type: types.OBJECT,
        children: [
          {
            key: 'column',
            label: 'Left Column Text',
            type: types.ARRAY,
            children: [
              {
                key: 'text',
                label: 'Text',
                type: types.TEXT,
              }
            ]
          },
        ],
      },
      {
        key: 'rightColumn',
        label: 'Right Column',
        type: types.OBJECT,
        children: [
          {
            key: 'column',
            label: 'Right Column Text',
            type: types.ARRAY,
            children: [
              {
                key: 'text',
                label: 'Text',
                type: types.TEXT,
              }
            ]
          },
        ],
      },
    ],
  },
  {
    key: 'confluence',
    label: 'Confluence Page',
    type: types.TINYMCE,
    options: {
      menubar: false,
      toolbar: false,
      height: 200,
    },
    tooltip: `The documentation for this widget can be found at https://confluence.faradayfuture.com/display/FW/Genesis+Widget+-+Background+Image+with+Text`
  }
];

const defaultProps: Props = {
  backgroundImage: {
    _: 'https://genesis-cdn.ff.com/assets/emails/ff-91_silver-default.jpg',
    md: 'https://genesis-cdn.ff.com/assets/emails/ff-91_silver-default.jpg',
    imageAlt: 'Front 3/4 view of the FF 91'
  },
  textColorTheme: 'LIGHT',
  backNavigation: false,
  variant1: {
    enabled: true,
    title: 'Freedom to Travel For Charging on the Go',
    description: `With 200kW of peak DC power capability, our FF 91 electric vehicle is compatible 
    with a vast network of public charging stations throughout the US and Canada. 
    Enjoy the convenience of charging your FF electric vehicle on the go while doing your
    part to support a greener earth and reduce carbon emissions.`,
    textBoxGradient: {
      linearGradient: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%);'
    }
  },
  variant2: {
    enabled: false,
    title: 'Magic All-In-One',
    subtitle: '4 Tech Systems',
    description: `All-terrain AI Body Control Technology System`,
    descriptionItems: [
      {
        title: 'All-AI',
        subtitle: 'Performance'
      },
      {
        title: 'All-Hyper',
        subtitle: 'Comfort'
      },
      {
        title: 'All-Ability',
        subtitle: 'High ground clearance, visibility and space'
      },
    ],
    position: {
      _: 'CENTER',
      md: 'BOTTOM'
    }
  },
  variant3: {
    enabled: false,
    title: 'The Ultimate AI TechLuxury Product & Technology Revolution New Four Trends',
    descriptionItems: [
      {
        title: 'All-AI',
      },
      {
        title: 'All-Hyper',
      },
      {
        title: 'All-Ability',
      },
      {
        title: 'Co-Creation',
      },
    ],
  },
  variant4: {
    enabled: false,
    title: 'Product & Technology Criteria',
    subtitle: 'All-Ability aiHypercar',
    descriptionItems: [
      {
        title: 'All-Ability',
        backgroundImage: 'https://genesis-cdn.ff.com/assets/pages/ai-hypercar/ptc-bg1-default.jpg',
        bulletPoints: [
          {
            text: 'Combination of Hypercar, sedans and SUV',
          },
          {
            text: 'Silicon-based All-In-One, combination of technology, platform and systems'
          }
        ]
      },
      {
        title: 'All-Hyper',
        backgroundImage: 'https://genesis-cdn.ff.com/assets/pages/ai-hypercar/ptc-bg2-default.jpg',
        bulletPoints: [
          {
            text: 'Hyper+ Multi-Vectoring',
          },
          {
            text: 'AI hardware capability Hyper'
          },
          {
            text: 'AI engine Hyper'
          },
        ],
      },
      {
        title: 'All-AI',
        backgroundImage: 'https://genesis-cdn.ff.com/assets/pages/ai-hypercar/ptc-bg3-default.jpg',
        bulletPoints: [
          {
            text: 'General AI + Personalized AI + 1-on-1 Bespoke Private AI',
          },
          {
            text: 'End to end AI'
          },
          {
            text: 'All scenarios AI'
          },
        ],
      },
    ],
  },
  variant5: {
    enabled: false,
    title: 'User Value and User Profile Criteria',
    leftColumn: {
      column: [
        {
          text: 'Entrepreneur'
        },
        {
          text: 'Adventurer'
        },
        {
          text: 'Scientist'
        },
        {
          text: 'Disruptor'
        }, 
      ]
    },
    rightColumn: {
      column: [
        {
          text: 'Creative'
        },
        {
          text: 'Visionary'
        },
        {
          text: 'Determined'
        },
        {
          text: 'Never give up'
        },
        {
          text: 'Drive transformation'
        },
      ]
    }
  },
  confluence: `
    <p>
      https://confluence.faradayfuture.com/display/FW/Genesis+Widget+-+Background+Image+with+Text
    </p>`
};

export default {
  title: 'Background Image with Text',
  id: 'BackgroundImageWithText',
  propsSchema,
  defaultProps,
}
